/**
 * useCampaignTemplates Hook
 *
 * Fetches and manages campaign templates.  Used by both the admin
 * campaign list (company templates) and the user campaign library
 * (company + own personal templates).
 *
 * @param {object} options
 * @param {boolean} options.activeOnly  - Only return active templates (default true)
 * @param {boolean} options.withStats   - Fetch from the stats view (admin dashboard)
 * @param {string}  options.forUserId   - Scope to company + this user's personal templates
 * @param {boolean} options.companyOnly - Scope to company templates only
 */

import { useState, useEffect, useCallback } from 'react';
import {
  fetchTemplates,
  fetchTemplateById,
  fetchTemplateStats,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  transformTemplateToCamelCase,
} from '../api/supabase/automations';

export function useCampaignTemplates({ activeOnly = true, withStats = false, forUserId = null, companyOnly = false } = {}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const baseData = await fetchTemplates({ activeOnly, forUserId, companyOnly });
      let templatesData = (baseData || []).map(transformTemplateToCamelCase);

      if (withStats) {
        // Stats are decorative — never let a stats failure blank the template
        // list (this exact failure hid every template on the Manage tab when
        // the stats view exceeded the API statement timeout, 2026-09-10).
        try {
          const stats = await fetchTemplateStats();
          const statsMap = new Map(
            (stats || []).map((s) => [s.template_id, s])
          );
          templatesData = templatesData.map((t) => {
            const s = statsMap.get(t.id);
            if (!s) return t;
            return {
              ...t,
              totalSubscribers: s.total_subscribers,
              activeSubscribers: s.active_subscribers,
              pendingQueueItems: s.pending_queue_items,
              completedQueueItems: s.completed_queue_items,
              failedQueueItems: s.failed_queue_items,
              fundedDeals: s.funded_deals,
              fundedVolume: s.funded_volume,
            };
          });
        } catch (statsErr) {
          console.warn('[useCampaignTemplates] stats unavailable — showing templates without stats:', statsErr);
        }
      }

      setTemplates(templatesData);
    } catch (err) {
      console.error('[useCampaignTemplates] load error:', err);
      setError(err.message || 'Failed to load campaign templates');
    } finally {
      setLoading(false);
    }
  }, [activeOnly, withStats, forUserId, companyOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const getById = useCallback(async (templateId) => {
    try {
      const data = await fetchTemplateById(templateId);
      return transformTemplateToCamelCase(data);
    } catch (err) {
      console.error('[useCampaignTemplates] getById error:', err);
      throw err;
    }
  }, []);

  const create = useCallback(async (values) => {
    try {
      const data = await createTemplate(values);
      const template = transformTemplateToCamelCase(data);
      setTemplates((prev) => [template, ...prev]);
      return template;
    } catch (err) {
      console.error('[useCampaignTemplates] create error:', err);
      throw err;
    }
  }, []);

  const update = useCallback(async (templateId, updates) => {
    try {
      const data = await updateTemplate(templateId, updates);
      const template = transformTemplateToCamelCase(data);
      setTemplates((prev) =>
        prev.map((t) => (t.id === templateId ? { ...t, ...template } : t))
      );
      return template;
    } catch (err) {
      console.error('[useCampaignTemplates] update error:', err);
      throw err;
    }
  }, []);

  const remove = useCallback(async (templateId, deletedBy = null) => {
    try {
      await deleteTemplate(templateId, deletedBy);
      setTemplates((prev) => prev.filter((t) => t.id !== templateId));
    } catch (err) {
      console.error('[useCampaignTemplates] remove error:', err);
      throw err;
    }
  }, []);

  return {
    templates,
    loading,
    error,
    reload: load,
    getById,
    create,
    update,
    remove,
  };
}
